// Blog / Journal source of truth.
// To add a new post, append to POSTS below. Each block in `body` is rendered
// as its own element in BlogPost.jsx.

export const POSTS = [
  {
    slug: "the-silent-crisis-in-urban-gardening",
    title: "The Silent Crisis in Urban Gardening",
    subtitle: "The race to the bottom nobody wants to talk about.",
    excerpt:
      "Aggressive price undercutting by funded startups is quietly devaluing the urban gardening industry — and the customers, gardeners and businesses it depends on.",
    author: "Sujal, Indoor Greens",
    published_at: "2025-12-01",
    reading_minutes: 6,
    tags: ["Industry", "Editorial", "Urban Gardening"],
    cover:
      "https://images.unsplash.com/photo-1615876063860-d971f6dca5dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxiaW9waGlsaWMlMjBpbnRlcmlvciUyMGRlc2lnbnxlbnwwfHx8fDE3NzkwOTUxNzN8MA&ixlib=rb-4.1.0&q=85",
    body: [
      { type: "lead", text:
        "The urban gardening industry is still in its infancy. It is full of passionate entrepreneurs, enthusiastic customers, and enormous potential. But beneath the excitement lies a challenge that very few are willing to discuss openly: aggressive price undercutting, especially by heavily funded startups." },
      { type: "p", text:
        "At first glance, lower prices appear to be a win for customers. They make gardening services seem more accessible and help companies acquire clients rapidly. But beneath this attractive surface lies a dangerous trend that could weaken the entire industry." },

      { type: "h2", text: "Growth at Any Cost" },
      { type: "p", text:
        "Funded startups often have the luxury of operating at a loss while chasing market share. Investor capital allows them to offer services at prices that are difficult, if not impossible, for sustainable businesses to match." },
      { type: "p", text:
        "This strategy may work in the short term. Customers flock to lower prices, competitors feel pressured to follow, and the startup celebrates impressive growth numbers." },
      { type: "p", text: "But eventually, the economics catch up." },
      { type: "p", text:
        "Investors expect profitability. Unit economics begin to matter. Discounts disappear. Prices rise. By then, however, the damage has already been done." },

      { type: "h2", text: "The Cost of Devaluing Expertise" },
      { type: "p", text: "Pricing is not just about revenue. It is also about perceived value." },
      { type: "p", text:
        "When professional urban gardening services are sold too cheaply, customers begin to believe that this is what the service is worth. The years of horticultural knowledge, plant care expertise, design thinking, maintenance planning, and personalized attention become invisible." },
      { type: "p", text:
        "Once the market begins to associate expertise with bargain pricing, it becomes incredibly difficult for every business, including the ones that undercut initially, to charge what the service is genuinely worth." },
      { type: "p", text:
        "This is not simply a pricing problem. It is a perception problem." },

      { type: "h2", text: "A Luxury Service Cannot Be Forced Into a Mass Market Overnight" },
      { type: "p", text: "There is another uncomfortable truth." },
      { type: "p", text:
        "Today, hiring a professional urban gardener is still a premium lifestyle choice. It is similar to hiring an interior designer, a personal trainer, or a private chef. Customers choose these services because they value convenience, expertise, and quality." },
      { type: "p", text:
        "Trying to position urban gardening as an affordable service for everyone, before the industry has reached the necessary scale and operational efficiency, is premature." },
      { type: "p", text: "Could that future arrive?" },
      { type: "p", text: "Absolutely." },
      { type: "p", text:
        "With better training systems, skilled manpower, standardized processes, improved technology, and economies of scale, urban gardening can become significantly more accessible over the next five years." },
      { type: "p", text: "But we are not there yet." },
      { type: "p", text:
        "Pretending otherwise creates unrealistic expectations for customers and unsustainable business models for service providers." },

      { type: "h2", text: "The Industry Pays the Price" },
      { type: "p", text:
        "The consequences extend far beyond one company's balance sheet." },
      { type: "ul", items: [
        "Healthy businesses struggle to retain skilled gardeners because margins shrink.",
        "Training investments reduce because companies cannot afford them.",
        "Service quality declines as businesses attempt to survive on thinner margins.",
        "Customers receive inconsistent experiences and gradually lose trust in professional gardening services altogether.",
      ] },
      { type: "p", text:
        "Ironically, the companies that initially benefited from undercutting may eventually face the same challenges they helped create." },
      { type: "pullquote", text: "A race to the bottom rarely has winners." },

      { type: "h2", text: "Sustainable Growth Is Better Than Fast Growth" },
      { type: "p", text:
        "The urban gardening industry deserves to grow. But it deserves to grow on strong foundations." },
      { type: "p", text:
        "Fair pricing allows businesses to invest in people, improve service quality, innovate, and build trust with customers. It creates careers instead of temporary jobs. It encourages professionalism instead of shortcuts." },
      { type: "p", text:
        "Most importantly, it ensures that businesses remain healthy long after investor funding or promotional offers disappear." },
      { type: "p", text:
        "Every young industry reaches a point where it must decide what kind of future it wants to build." },
      { type: "p", text: "Urban gardening has reached that moment." },
      { type: "p", text:
        "The question is not whether we can make gardening more accessible. We absolutely should." },
      { type: "p", text:
        "The real question is whether we do it by creating genuine efficiencies and developing the industry's capabilities, or by simply charging less than the service is worth." },
      { type: "p", text: "The first approach builds an industry." },
      { type: "p", text: "The second only builds temporary market share." },
    ],
  },
];

export const getPostBySlug = (slug) => POSTS.find((p) => p.slug === slug);
